import { TrashIcon } from '@heroicons/react/24/outline';
import { default as Modal } from 'antd/es/modal';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useBoolean } from 'usehooks-ts';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { formatDecimalAmount } from 'utils/formatters/formatCurrency';
import { hasValue } from 'utils/hasValue';

import { PayoutsTable } from '@pages/Payouts/PayoutsTable';
import { TableLayout } from '@templates/TableLayout/TableLayout';
import {
  useGetCustomerConfigurations,
  useGetDisbursementPayoutBatchesBatchId,
  usePostDisbursementPayoutBatchesBatchIdApprove,
  usePostDisbursementPayoutBatchesBatchIdReject
} from 'api';
import type { PayoutBatch } from 'api';

import { selectUserId } from 'reduxLocal/slices/authSlice';

import type { Reviewer } from 'models/ApprovalWorkFlow';

import { useOrganisationId } from 'hooks/context';
import { useOrgNavigate } from 'hooks/useOrgNavigate';
import { usePermissions } from 'hooks/usePermissions';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import { BatchPayoutsHeader } from './BatchPayoutsHeader';
import { BatchDraftPayoutsList } from './BatchPayoutsList/BatchDraftPayoutsList';

import { Loading } from '../Loading';

export const BatchPayouts = () => {
  const userId = useSelector(selectUserId);
  if (!userId) throw new Error('Missing user id');
  const openNotification = useOpenNotification();

  const { batchId } = useParams();
  const organisationId = useOrganisationId<number>();

  const navigate = useOrgNavigate();
  const { isPermitted } = usePermissions();

  const {
    isError: isOrgSettingsError,
    isSuccess: isOrgSettingsSuccess,
    isLoading: isOrgSettingsLoading,
    data: { data: orgSettings } = {}
  } = useGetCustomerConfigurations(organisationId);

  const { mutate: approveBatch } =
    usePostDisbursementPayoutBatchesBatchIdApprove();

  const { mutate: rejectBatch } =
    usePostDisbursementPayoutBatchesBatchIdReject();

  const {
    data: { data: batchDetails } = {},
    isLoading: isBatchLoading,
    isError: isBatchError,
    isSuccess: isBatchSuccess
  } = useGetDisbursementPayoutBatchesBatchId(organisationId, Number(batchId), {
    query: {
      enabled: !!batchId
    }
  });

  const navigatePostApproval = useCallback(() => {
    const remainingApprovals = batchDetails?.approvals
      ? Number(orgSettings?.batch_number_of_approvers) -
        batchDetails.approvals.length
      : orgSettings?.batch_number_of_approvers;

    if (remainingApprovals === 1) {
      // Redirect to approved page if the user is the last one to approve
      navigate('/batches?status=completed');
    } else {
      // Redirect to pending approval if other approvals are still required
      navigate(`/batches/${batchDetails?.id}?status=waiting_review`);
    }
  }, [
    batchDetails?.approvals,
    batchDetails?.id,
    navigate,
    orgSettings?.batch_number_of_approvers
  ]);

  const {
    value: isDeclineDialogVisible,
    toggle: toggleDeclineDialog,
    setValue: setIsDeclineDialogVisible
  } = useBoolean(false);

  const handleApprove = useCallback(() => {
    approveBatch(
      {
        organisationId,
        batchId: Number(batchId)
      },
      {
        onSuccess: () => {
          openNotification({
            kind: 'success',
            message: 'Batch has been approved',
            key: 'batch-approved'
          });
          navigatePostApproval();
        },
        onError: () => {
          openNotification({
            kind: 'error',
            message: 'Error while approving the batch'
          });
        }
      }
    );
  }, [
    approveBatch,
    organisationId,
    batchId,
    openNotification,
    navigatePostApproval
  ]);

  const handleReject = useCallback(() => {
    rejectBatch(
      {
        organisationId,
        batchId: Number(batchId)
      },
      {
        onSuccess: () => {
          openNotification({
            kind: 'success',
            message: 'Batch has been declined',
            icon: <TrashIcon className="stroke-content-positive-soft" />
          });
          navigate('/batches?status=rejected');
        },
        onError: () => {
          openNotification({
            kind: 'error',
            message: 'Error while declining the batch'
          });
        }
      }
    );
  }, [rejectBatch, organisationId, batchId, openNotification, navigate]);

  if (
    !hasValue(orgSettings) ||
    !hasValue(batchId) ||
    !hasValue(userId) ||
    !hasValue(organisationId)
  ) {
    return null;
  }

  const isApprovalEnabled = Number(orgSettings.batch_number_of_approvers) > 0;

  const isApprovedByUser = batchDetails?.approvals?.some(
    (approval: Reviewer) => approval.user_id === userId
  );

  const formatBatchDetails = (batchDetails: PayoutBatch) => {
    const localInstrument = batchDetails?.local_instrument || '';
    const countryName = batchDetails?.country?.name || '';

    let formattedAmount = '';
    if (batchDetails?.total_amount) {
      formattedAmount = formatDecimalAmount(batchDetails.total_amount);
    }

    const currencyIso = batchDetails?.currency?.iso || '';

    return `${localInstrument}, ${countryName}, ${formattedAmount} ${currencyIso}`;
  };

  const canApproveBase =
    isApprovalEnabled &&
    !isApprovedByUser &&
    batchDetails !== undefined &&
    batchDetails.status === 'waiting_review' &&
    !batchDetails.rejections?.length &&
    batchDetails.creator_id !== userId;

  const canApprove = canApproveBase && isPermitted('payout_batches:review');

  return (
    <>
      {(isBatchLoading || isOrgSettingsLoading) && <Loading />}
      {(isBatchError || isOrgSettingsError) && (
        <div className="flex h-full items-center justify-center">
          Something went wrong
        </div>
      )}

      {isBatchSuccess && isOrgSettingsSuccess && (
        <TableLayout
          header={
            <BatchPayoutsHeader
              toggleDeclineDialog={toggleDeclineDialog}
              batchDetails={batchDetails}
              isApprovalEnabled={isApprovalEnabled}
              userId={userId}
              requiredNumberOfApprovals={Number(
                orgSettings.batch_number_of_approvers
              )}
              canApprove={canApprove}
              formatBatchDetails={formatBatchDetails}
              handleApprove={handleApprove}
            />
          }
          content={
            <>
              <Modal
                open={isDeclineDialogVisible}
                onCancel={() => setIsDeclineDialogVisible(false)}
                footer={
                  <div className="grid w-full grid-cols-2 gap-4">
                    <SecondaryButton
                      onClick={() => setIsDeclineDialogVisible(false)}
                    >
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton danger onClick={handleReject}>
                      Decline
                    </PrimaryButton>
                  </div>
                }
              >
                <div className="text-lg font-bold">Decline batch?</div>
                <div>
                  This batch will be declined and the enclosed payouts will not
                  be processed.
                </div>
              </Modal>

              {batchDetails?.status === 'completed' ? (
                <PayoutsTable batchId={batchId} />
              ) : (
                <BatchDraftPayoutsList batchId={batchId} />
              )}
            </>
          }
        />
      )}
    </>
  );
};
